import {
  IApiKeyRepository,
  IAutoSubmitConfigAPIRepository,
  IAutoSubmitRecordApiRepository,
  IBulkElementApiRepository,
  IBulkRawMaterialApiRepository,
  IBulkSampleApiRepository,
  ICharInstrumentApiRepository,
  ICommunicationApiRepository,
  IDataPackageApiRepository,
  IImportRecordApiRepository,
  IMaterialApiRepository,
  IMetaDataConfigApiRepository,
  IProjectApiRepository,
  IProjectActivityApiRepository,
  IPublicationApiRepository,
  IRecipeFavoriteApiRepository,
  IRecipeTemplateApiRepository,
  IRoleApiRepository,
  ISampleImportApiRepository,
  ISampleImportConfigAPIRepository,
  ISampleReportApiRepository,
  ISampleReportConfigApiRepository,
  ISampleApiRepository,
  ISampleRequestApiRepository,
  ISampleActivityApiRepository,
  ISampleSnapshotApiRepository,
  ISampleStatisticsApiRepository,
  ISelectListApiRepository,
  IShippingApiRepository,
  ISimulationDatasetApiRepository,
  ISubstrateApiRepository,
  ISynInstrumentApiRepository,
  ISynInstrumentTypeApiRepository,
  ISynTechniqueApiRepository,
  ITheoryActivityApiRepository,
  IUserRepository,
  ISystemConfigRepository,
  ApiKeyRepository,
  BulkElementApiRepository,
  BulkRawMaterialApiRepository,
  BulkSampleApiRepository,
  CharInstrumentApiRepository,
  CommunicationApiRepository,
  DataPackageApiRepository,
  MaterialApiRepository,
  MetaDataConfigApiRepository,
  ProjectActivityApiRepository,
  ProjectApiRepository,
  PublicationApiRepository,
  RecipeFavoriteApiRepository,
  RecipeTemplateApiRepository,
  RoleApiRepository,
  SampleImportApiRepository,
  SampleImportConfigAPIRepository,
  SampleReportApiRepository,
  SampleReportConfigApiRepository,
  SampleActivityApiRepository,
  SampleRequestApiRepository,
  SampleApiRepository,
  SampleSnapshotApiRepository,
  SampleStatisticsApiRepository,
  SelectListApiRepository,
  ShippingApiRepository,
  SubstrateApiRepository,
  SynInstrumentApiRepository,
  SynInstrumentTypeApiRepository,
  SynTechniqueApiRepository,
  SystemConfigRepository,
  TheoryActivityApiRepository,
  UserRepository,
  SimulationDatasetApiRepository,
  ICharacterizationTechniqueRepository,
  ICharacterizationConfigAPIRepository,
  ICharacterizationUploadRecordApiRepository,
  CharacterizationTechniqueRepository,
  CharacterizationConfigAPIRepository,
  CharacterizationUploadRecordApiRepository,
  AutoSubmitConfigAPIRepository,
  AutoSubmitRecordApiRepository,
  ImportRecordApiRepository,
  IFileSourceAPIRepository,
  FileSourceAPIRepository,
  IFileSharingConfigAPIRepository,
  FileSharingConfigAPIRepository,
} from './ApiClient';
import { IAuthContext } from './AuthorizedApiBase';
import { HttpClient } from './HttpClient';
import { log } from '@/lib/utils/logger';
import { createBackendUrl } from '@/lib/utils/urlUtils';

export interface IBackend {
  createBackendUrl(relativeUrl: string, params?: Record<string, string | undefined | null>): URL;
  apiKey: IApiKeyRepository;
  bulkElement: IBulkElementApiRepository;
  bulkRawMaterial: IBulkRawMaterialApiRepository;
  bulkSample: IBulkSampleApiRepository;
  characterizationTechnique: ICharacterizationTechniqueRepository;
  characterizationInstrument: ICharInstrumentApiRepository;
  communication: ICommunicationApiRepository;
  data: IDataPackageApiRepository;
  autoSubmitConfig: IAutoSubmitConfigAPIRepository;
  autoSubmitRecord: IAutoSubmitRecordApiRepository;
  characterizationConfig: ICharacterizationConfigAPIRepository;
  characterizationUploadRecord: ICharacterizationUploadRecordApiRepository;
  importRecord: IImportRecordApiRepository;
  material: IMaterialApiRepository;
  metaDataConfig: IMetaDataConfigApiRepository;
  project: IProjectApiRepository;
  projectActivity: IProjectActivityApiRepository;
  publication: IPublicationApiRepository;
  recipeFavorite: IRecipeFavoriteApiRepository;
  recipeTemplate: IRecipeTemplateApiRepository;
  role: IRoleApiRepository;
  sampleImport: ISampleImportApiRepository;
  sampleImportConfig: ISampleImportConfigAPIRepository;
  sampleReport: ISampleReportApiRepository;
  sampleReportConfig: ISampleReportConfigApiRepository;
  sample: ISampleApiRepository;
  sampleRequest: ISampleRequestApiRepository;
  sampleActivity: ISampleActivityApiRepository;
  sampleSnapshot: ISampleSnapshotApiRepository;
  sampleStat: ISampleStatisticsApiRepository;
  selectList: ISelectListApiRepository;
  shipping: IShippingApiRepository;
  simulationDataset: ISimulationDatasetApiRepository;
  substrate: ISubstrateApiRepository;
  synthesisInstrument: ISynInstrumentApiRepository;
  synthesisInstrumentType: ISynInstrumentTypeApiRepository;
  synthesisTechnique: ISynTechniqueApiRepository;
  systemConfig: ISystemConfigRepository;
  theoryActivity: ITheoryActivityApiRepository;
  user: IUserRepository;
  fileSource: IFileSourceAPIRepository;
  fileSharingConfig: IFileSharingConfigAPIRepository;
}

export function BackendApi(authContext: IAuthContext, baseUrl: string): IBackend {
  log.debug('[BackendApi] Creating BackendApi with baseUrl:', baseUrl);
  const http = new HttpClient();
  http.setBaseUrl(baseUrl);
  const lazy = <T>(creator: () => T): (() => T) => {
    let instance: T | undefined;
    return () => {
      if (!instance) instance = creator();
      return instance;
    };
  };

  const backend: Partial<IBackend> = {
    createBackendUrl: (relativeUrl: string, params: Record<string, string | undefined | null> = {}) =>
      createBackendUrl(baseUrl, relativeUrl, params),
  };

  const defineLazy = <K extends keyof IBackend>(key: K, creator: () => IBackend[K]) => {
    Object.defineProperty(backend, key, {
      get: lazy(creator),
      enumerable: true,
      configurable: false,
    });
  };

  defineLazy('apiKey', () => new ApiKeyRepository(authContext, baseUrl, http));
  defineLazy('bulkElement', () => new BulkElementApiRepository(authContext, baseUrl, http));
  defineLazy('bulkRawMaterial', () => new BulkRawMaterialApiRepository(authContext, baseUrl, http));
  defineLazy('bulkSample', () => new BulkSampleApiRepository(authContext, baseUrl, http));
  defineLazy('characterizationTechnique', () => new CharacterizationTechniqueRepository(authContext, baseUrl, http));
  defineLazy('characterizationInstrument', () => new CharInstrumentApiRepository(authContext, baseUrl, http));
  defineLazy('communication', () => new CommunicationApiRepository(authContext, baseUrl, http));
  defineLazy('data', () => new DataPackageApiRepository(authContext, baseUrl, http));
  defineLazy('autoSubmitConfig', () => new AutoSubmitConfigAPIRepository(authContext, baseUrl, http));
  defineLazy('autoSubmitRecord', () => new AutoSubmitRecordApiRepository(authContext, baseUrl, http));
  defineLazy('characterizationConfig', () => new CharacterizationConfigAPIRepository(authContext, baseUrl, http));
  defineLazy(
    'characterizationUploadRecord',
    () => new CharacterizationUploadRecordApiRepository(authContext, baseUrl, http)
  );
  defineLazy('importRecord', () => new ImportRecordApiRepository(authContext, baseUrl, http));
  defineLazy('material', () => new MaterialApiRepository(authContext, baseUrl, http));
  defineLazy('metaDataConfig', () => new MetaDataConfigApiRepository(authContext, baseUrl, http));
  defineLazy('project', () => new ProjectApiRepository(authContext, baseUrl, http));
  defineLazy('projectActivity', () => new ProjectActivityApiRepository(authContext, baseUrl, http));
  defineLazy('publication', () => new PublicationApiRepository(authContext, baseUrl, http));
  defineLazy('recipeFavorite', () => new RecipeFavoriteApiRepository(authContext, baseUrl, http));
  defineLazy('recipeTemplate', () => new RecipeTemplateApiRepository(authContext, baseUrl, http));
  defineLazy('role', () => new RoleApiRepository(authContext, baseUrl, http));
  defineLazy('sampleImport', () => new SampleImportApiRepository(authContext, baseUrl, http));
  defineLazy('sampleImportConfig', () => new SampleImportConfigAPIRepository(authContext, baseUrl, http));
  defineLazy('sampleReport', () => new SampleReportApiRepository(authContext, baseUrl, http));
  defineLazy('sampleReportConfig', () => new SampleReportConfigApiRepository(authContext, baseUrl, http));
  defineLazy('sample', () => new SampleApiRepository(authContext, baseUrl, http));
  defineLazy('sampleRequest', () => new SampleRequestApiRepository(authContext, baseUrl, http));
  defineLazy('sampleActivity', () => new SampleActivityApiRepository(authContext, baseUrl, http));
  defineLazy('sampleSnapshot', () => new SampleSnapshotApiRepository(authContext, baseUrl, http));
  defineLazy('sampleStat', () => new SampleStatisticsApiRepository(authContext, baseUrl, http));
  defineLazy('selectList', () => new SelectListApiRepository(authContext, baseUrl, http));
  defineLazy('simulationDataset', () => new SimulationDatasetApiRepository(authContext, baseUrl, http));
  defineLazy('shipping', () => new ShippingApiRepository(authContext, baseUrl, http));
  defineLazy('substrate', () => new SubstrateApiRepository(authContext, baseUrl, http));
  defineLazy('synthesisInstrument', () => new SynInstrumentApiRepository(authContext, baseUrl, http));
  defineLazy('synthesisInstrumentType', () => new SynInstrumentTypeApiRepository(authContext, baseUrl, http));
  defineLazy('synthesisTechnique', () => new SynTechniqueApiRepository(authContext, baseUrl, http));
  defineLazy('systemConfig', () => new SystemConfigRepository(authContext, baseUrl, http));
  defineLazy('theoryActivity', () => new TheoryActivityApiRepository(authContext, baseUrl, http));
  defineLazy('user', () => new UserRepository(authContext, baseUrl, http));
  defineLazy('fileSource', () => new FileSourceAPIRepository(authContext, baseUrl, http));
  defineLazy('fileSharingConfig', () => new FileSharingConfigAPIRepository(authContext, baseUrl, http));

  return backend as IBackend;
}
